import { Component, OnInit, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FilestackService } from '@filestack/angular';
import { combineLatest, NEVER, Observable } from 'rxjs';
import { map, switchMap, shareReplay, take, catchError, withLatestFrom, tap } from 'rxjs/operators';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Security, ClientOptions } from 'filestack-js';

import MetaFileLink, { MediaType } from 'src/app/models/metaFileLink.model';
import { AppSettingsService } from 'src/app/services/app-settings.service';
import { StorageObjectReqRes } from '../../models/StorageObjectReqRes.model';
import { StorageObjectDataService } from 'src/app/services/shared/storage-object-data.service';
import { RoutingService } from 'src/app/services/routing.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { TerraUtils } from '../../TerraUtils';
import { HoldingDiscriminator } from '../../enums/HoldingDiscriminator.enum';
import {Location} from '@angular/common';

@Component({
  selector: 'terra-file-preview-page',
  templateUrl: './file-preview-page.component.html',
  styleUrls: ['./file-preview-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilePreviewPageComponent extends OnDestroyMixin implements OnInit, AfterViewInit {

  // enums
  GeneralMediaType = MediaType;

  storageObjectId$ = this.activatedRoute.params.pipe(
    map(params => +params.storageObjectId));

  holdingId$ = this.activatedRoute.params.pipe(
    map(params => +params.holdingId));

  holdingDiscriminator$ = this.activatedRoute.data.pipe(map((data: { holdingDiscriminator: HoldingDiscriminator }) => data.holdingDiscriminator));

  storageObject$ = this.storageObjectId$.pipe(
    switchMap(id => this.storageObjectDataService.getFile(id)),
    catchError(error => {
      this.snackbarService.showGeneralMessage('File is not avaiable', 4);
      this.close();
      return NEVER;
    }),
    shareReplay(1));

  generalMediaType$ = this.storageObject$.pipe(
    map(storageObject => MetaFileLink.GetMediaType(storageObject.metaFileLink)), shareReplay(1));

  fullUrlWithCredentials$ = this.storageObject$.pipe(
    map(storageObject => StorageObjectReqRes.getFilestackUrlWithCredentials(storageObject)), shareReplay(1));

  originFundId$: Observable<number> = this.activatedRoute
    .queryParams.pipe(map(query => +query[this.routingService.originFundParamName] || null));

  constructor(
    private routingService: RoutingService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private appSettingsService: AppSettingsService,
    private filestackService: FilestackService,
    private storageObjectDataService: StorageObjectDataService,
    private snackbarService: SnackbarService,
    private _location: Location) {
      super();
  }

  ngOnInit() {
  }

  ngAfterViewInit(): void {
    combineLatest([this.storageObject$, this.generalMediaType$])
      .pipe(
        take(1),
        untilComponentDestroyed(this))
      .subscribe(([storageObject, mediaType]) => {
        switch (mediaType) {
          case MediaType.Image:
          case MediaType.Pdf:
          case MediaType.Presentation:
          case MediaType.Spreadsheet:
          case MediaType.TextDocument:
            // The filestack previewr can display any file that they can convert to pdf
            this.fileStackPreview(storageObject);
            break;
          case MediaType.Video:
            break;
          case MediaType.Audio:
            break;
        }
      });
  }

  close() {
    this.holdingId$.pipe(
      take(1),
      withLatestFrom(this.holdingDiscriminator$),
      tap(([holdingId, holdingDiscriminator]) => this.router.navigate([this.routingService.lpHoldingPageWithTab(holdingId, holdingDiscriminator, 'documents')])))
    .subscribe();
  }

  isAudioOrVideoSupportedForPreview(file: StorageObjectReqRes, mediaType: MediaType): boolean {
    return TerraUtils.isAudioOrVideoSupportedForPreview(file, mediaType);
  }

  dowload(storageObject: StorageObjectReqRes) {
    if (!storageObject) {
      return;
    }
    this.storageObjectDataService.getDownloadToken(storageObject.id).subscribe(
      () => { },
      error => {
        this.snackbarService.showGeneralMessage('Error: Could not download file');
      }
    );
  }

  private fileStackPreview(storageObject: StorageObjectReqRes) {
    const clientOptions = {
      security: {
        policy: storageObject.filestackPolicy,
        signature: storageObject.filestackSignature
      } as Security
    } as ClientOptions;

    this.filestackService.init(this.appSettingsService.fileStackApiKey, clientOptions);

    this.filestackService.preview(storageObject.filestackHandle,
      {
        id: 'filePreviewFrame',
        // css: `https://cdn.filestackcontent.com/security=policy:eyJleHBpcnkiOiAxNTg4NjkwMjgwLCAiY2FsbCI6IFsicmVhZCJdLCAiaGFuZGxlIjogImxGVzU4aXFCUTRPZXVMcVR0T2xUIn0=,signature:665a6ab46af33654455aaedc9095c66adad35c7217be6bf7530e77d30ba505ec/lFW58iqBQ4OeuLqTtOlT`
      });
  }
}
